/**
 * ImageUploader Component
 * 
 * Reusable component for uploading images with preview
 * Handles file selection, validation, upload progress, and error display
 * 
 * Usage:
 * <ImageUploader
 *   currentImageUrl={profilePhoto}
 *   onUploadSuccess={(url) => setProfilePhoto(url)}
 *   buttonText="Update Photo"
 *   imageSize={48}
 * />
 */

import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Avatar,
    CircularProgress,
    Alert,
    Stack,
    Typography
} from '@mui/material';
import { useImageUpload } from '../../hooks/useImageUpload';

interface ImageUploaderProps {
    currentImageUrl?: string | null;
    onUploadSuccess?: (imageUrl: string) => void;
    onUploadError?: (error: string) => void;
    buttonText?: string;
    imageSize?: number;
    imageShape?: 'square' | 'circle';
    maxSizeMB?: number;
    allowedTypes?: string[];
    folderType?: string; // Added folderType prop
    imageType?: string; // Added imageType prop
    showRemoveButton?: boolean;
    onRemove?: () => void;
    showHelperText?: boolean;
    helperText?: string;
}

export const ImageUploader = ({
    currentImageUrl,
    onUploadSuccess,
    onUploadError,
    buttonText = 'Upload',
    imageSize = 64,
    imageShape = 'square',
    maxSizeMB = 5,
    allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'],
    folderType = 'profile-images', // Default to profile-images
    imageType = 'profile', // Default to profile
    showRemoveButton = false,
    onRemove,
    showHelperText = true,
    helperText
}: ImageUploaderProps) => {
    const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);

    // Reset localImageUrl when currentImageUrl changes from outside (e.g., from API)
    // This ensures we use the prop value when it's updated from the API
    useEffect(() => {
        if (currentImageUrl) {
            // Only clear localImageUrl if it's different from currentImageUrl
            // This allows the prop to take precedence when it changes
            if (localImageUrl && localImageUrl !== currentImageUrl) {
                setLocalImageUrl(null);
            }
        }
    }, [currentImageUrl]);

    const { uploadImage, isUploading, error, clearError } = useImageUpload({
        maxSizeMB,
        allowedTypes,
        folderType, // Pass folderType to hook
        imageType, // Pass imageType to hook
        onSuccess: (result) => {
            if (result.imageUrl) {
                setLocalImageUrl(result.imageUrl);
                onUploadSuccess?.(result.imageUrl);
            }
        },
        onError: (errorMessage) => {
            onUploadError?.(errorMessage);
        }
    });

    const displayImageUrl = localImageUrl || currentImageUrl;

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            clearError();
            await uploadImage(file);
        }
        // Clear input so same file can be selected again
        event.target.value = '';
    };

    const handleRemove = () => {
        setLocalImageUrl(null);
        onRemove?.();
    };

    const borderRadius = imageShape === 'circle' ? '50%' : '8px';

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={3} alignItems="center">
                {/* Image Preview */}
                <Box
                    sx={{
                        width: imageSize,
                        height: imageSize,
                        borderRadius: borderRadius,
                        border: '2px dashed #D1D5DB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        bgcolor: displayImageUrl ? 'white' : '#F9FAFB',
                        position: 'relative'
                    }}
                >
                    {isUploading ? (
                        <CircularProgress size={24} sx={{ color: '#4F46E5' }} />
                    ) : displayImageUrl ? (
                        <Avatar
                            src={displayImageUrl}
                            sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: imageShape === 'circle' ? '50%' : '8px'
                            }}
                        />
                    ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                            No Image
                        </Typography>
                    )}
                </Box>

                {/* Action Buttons */}
                <Stack direction="row" spacing={2}>
                    {showRemoveButton && displayImageUrl && !isUploading && (
                        <Button
                            variant="text"
                            onClick={handleRemove}
                            sx={{
                                textTransform: 'none',
                                color: '#9CA3AF',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                '&:hover': {
                                    color: '#6B7280'
                                }
                            }}
                        >
                            Remove
                        </Button>
                    )}
                    <Button
                        variant="text"
                        component="label"
                        disabled={isUploading}
                        sx={{
                            textTransform: 'none',
                            color: '#4F46E5',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            '&:hover': {
                                color: '#4338CA'
                            },
                            '&.Mui-disabled': {
                                color: '#9CA3AF'
                            }
                        }}
                    >
                        {isUploading ? 'Uploading...' : displayImageUrl ? 'Change' : buttonText}
                        <input
                            type="file"
                            hidden
                            accept={allowedTypes.join(',')}
                            onChange={handleFileChange}
                            disabled={isUploading}
                        />
                    </Button>
                </Stack>
            </Stack>

            {/* Error Message */}
            {error && (
                <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
                    {error}
                </Alert>
            )}

            {/* Helper Text */}
            {showHelperText && !error && (
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        color: '#6B7280',
                        fontWeight: 500
                    }}
                >
                    {helperText || `Recommended size: ${imageSize * 8}x${imageSize * 8}px. Max file size: ${maxSizeMB}MB. Supports PNG and JPG.`}
                </Typography>
            )}
        </Stack>
    );
};
